import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Save, ShieldAlert } from "lucide-react";
import {
  getCompliancePublicConfig,
  updateComplianceVersions,
  updateJurisdictionsConfig,
  listSuitabilityAcknowledgments,
  listStaleAcceptances,
} from "@/lib/compliance/config.functions";
import { COUNTRY_LIST } from "@/lib/compliance/constants";
import { refreshPerformanceStats } from "@/lib/performance.functions";

export const Route = createFileRoute("/_authenticated/admin/compliance")({
  component: CompliancePage,
});

function CompliancePage() {
  const qc = useQueryClient();
  const loadCfg = useServerFn(getCompliancePublicConfig);
  const saveJur = useServerFn(updateJurisdictionsConfig);
  const saveVer = useServerFn(updateComplianceVersions);
  const loadAcks = useServerFn(listSuitabilityAcknowledgments);
  const loadStale = useServerFn(listStaleAcceptances);
  const refreshPerf = useServerFn(refreshPerformanceStats);

  const { data: cfg } = useQuery({ queryKey: ["compliance", "cfg"], queryFn: () => loadCfg() });
  const { data: acks } = useQuery({ queryKey: ["compliance", "acks"], queryFn: () => loadAcks() });
  const { data: stale } = useQuery({ queryKey: ["compliance", "stale"], queryFn: () => loadStale() });

  // Local form state mirrored from cfg
  const [mode, setMode] = useState<"warn" | "block">("warn");
  const [warning, setWarning] = useState<string[]>([]);
  const [blocked, setBlocked] = useState<string[]>([]);
  const [warningInput, setWarningInput] = useState("");
  const [blockedInput, setBlockedInput] = useState("");

  const [riskV, setRiskV] = useState("");
  const [discV, setDiscV] = useState("");
  const [termsV, setTermsV] = useState("");

  useEffect(() => {
    if (!cfg) return;
    setMode(cfg.jurisdictions.mode);
    setWarning(cfg.jurisdictions.warning);
    setBlocked(cfg.jurisdictions.blocked);
    setRiskV(cfg.versions.risk_disclosure);
    setDiscV(cfg.versions.disclaimer);
    setTermsV(cfg.versions.terms);
  }, [cfg]);

  const saveJurMut = useMutation({
    mutationFn: () => saveJur({ data: { mode, warning, blocked } }),
    onSuccess: () => {
      toast.success("Jurisdiction policy saved");
      qc.invalidateQueries({ queryKey: ["compliance"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const saveVerMut = useMutation({
    mutationFn: () =>
      saveVer({ data: { risk_disclosure: riskV, disclaimer: discV, terms: termsV } }),
    onSuccess: () => {
      toast.success("Versions updated — users will be asked to re-accept");
      qc.invalidateQueries({ queryKey: ["compliance"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const refreshMut = useMutation({
    mutationFn: () => refreshPerf(),
    onSuccess: () => toast.success("Performance stats refreshed"),
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Refresh failed"),
  });

  function addCode(target: "w" | "b", raw: string) {
    const c = raw.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(c)) {
      toast.error("Use 2-letter ISO country code");
      return;
    }
    if (target === "w") {
      if (!warning.includes(c)) setWarning([...warning, c]);
      setWarningInput("");
    } else {
      if (!blocked.includes(c)) setBlocked([...blocked, c]);
      setBlockedInput("");
    }
  }

  function exportCsv() {
    const rows = (acks?.entries ?? []).map((e) => {
      const meta = (e.metadata ?? {}) as Record<string, unknown>;
      return [
        e.created_at,
        e.actor_id ?? "",
        e.actor_email ?? "",
        e.action,
        String(meta.country_selected ?? ""),
        String(meta.country_detected_from_ip ?? ""),
        String(meta.experience ?? ""),
        String(meta.risk_disclosure_version ?? ""),
        String(meta.disclaimer_version ?? ""),
        String(meta.jurisdiction_warning_shown ?? ""),
        String(meta.jurisdiction_acknowledged ?? ""),
        String(meta.us_acknowledged ?? ""),
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv =
      "created_at,actor_id,actor_email,action,country_selected,country_detected_from_ip,experience,risk_disclosure_version,disclaimer_version,jurisdiction_warning_shown,jurisdiction_acknowledged,us_acknowledged\n" +
      rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="font-display text-2xl font-bold">Compliance controls</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage disclosure versions, restricted jurisdictions, and view acknowledgment logs.
        </p>
      </div>

      {/* Versions */}
      <Card className="p-5">
        <h2 className="mb-3 text-base font-semibold">Document versions</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Bumping any of these will require every user to re-accept on next visit.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label>Risk disclosure</Label>
            <Input value={riskV} onChange={(e) => setRiskV(e.target.value)} placeholder="v1.0" />
          </div>
          <div>
            <Label>Disclaimer</Label>
            <Input value={discV} onChange={(e) => setDiscV(e.target.value)} placeholder="v1.0" />
          </div>
          <div>
            <Label>Terms</Label>
            <Input value={termsV} onChange={(e) => setTermsV(e.target.value)} placeholder="v1.0" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Last updated: {cfg?.versions.updated_at ? new Date(cfg.versions.updated_at).toLocaleString() : "—"}
          </span>
          <Button onClick={() => saveVerMut.mutate()} disabled={saveVerMut.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> Save versions
          </Button>
        </div>
      </Card>

      {/* Jurisdictions */}
      <Card className="p-5">
        <h2 className="mb-3 text-base font-semibold">Restricted &amp; warning jurisdictions</h2>
        <div className="mb-4 flex items-center justify-between rounded-md border border-border/60 bg-card/40 px-3 py-2">
          <div>
            <Label className="text-sm">Default policy mode</Label>
            <p className="text-xs text-muted-foreground">
              {mode === "warn"
                ? "Warning countries show a notice and require acknowledgment."
                : "Warning countries are treated as blocked until you adjust per-country."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">Warn</span>
            <Switch
              checked={mode === "block"}
              onCheckedChange={(v) => setMode(v ? "block" : "warn")}
            />
            <span className="text-xs">Block</span>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <CountryListEditor
            title="Warning (acknowledgment required)"
            codes={warning}
            input={warningInput}
            setInput={setWarningInput}
            onAdd={(c) => addCode("w", c)}
            onRemove={(c) => setWarning(warning.filter((x) => x !== c))}
            tone="warn"
          />
          <CountryListEditor
            title="Blocked (no access)"
            codes={blocked}
            input={blockedInput}
            setInput={setBlockedInput}
            onAdd={(c) => addCode("b", c)}
            onRemove={(c) => setBlocked(blocked.filter((x) => x !== c))}
            tone="sell"
          />
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={() => saveJurMut.mutate()} disabled={saveJurMut.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> Save jurisdiction policy
          </Button>
        </div>
      </Card>

      {/* Performance refresh */}
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Performance dashboard</h2>
            <p className="text-xs text-muted-foreground">
              Manually recompute the public /performance aggregates.
            </p>
          </div>
          <Button
            onClick={() => refreshMut.mutate()}
            disabled={refreshMut.isPending}
            variant="outline"
          >
            {refreshMut.isPending ? "Refreshing…" : "Refresh now"}
          </Button>
        </div>
      </Card>

      {/* Stale acceptances */}
      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Users with stale acceptance</h2>
          <Badge variant="outline">{stale?.users.length ?? 0} users</Badge>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Users who accepted an older risk disclosure or disclaimer than the current version.
        </p>
        <div className="max-h-72 overflow-y-auto rounded-md border border-border/60">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card/80 backdrop-blur">
              <tr className="border-b border-border/60 text-left">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Risk version</th>
                <th className="px-3 py-2">Disclaimer version</th>
                <th className="px-3 py-2">Confirmed</th>
              </tr>
            </thead>
            <tbody>
              {(stale?.users ?? []).map((u) => (
                <tr key={u.id} className="border-b border-border/60">
                  <td className="px-3 py-1.5">{u.email}</td>
                  <td className="px-3 py-1.5 font-mono">{u.risk_disclosure_version ?? "—"}</td>
                  <td className="px-3 py-1.5 font-mono">{u.disclaimer_version ?? "—"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {u.suitability_confirmed_at
                      ? new Date(u.suitability_confirmed_at).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
              {(stale?.users ?? []).length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                    All users are on the latest version.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Audit log */}
      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Recent acknowledgments</h2>
          <div className="flex items-center gap-2">
            <Badge variant="outline">{acks?.entries.length ?? 0} entries</Badge>
            <Button size="sm" variant="outline" onClick={exportCsv}>
              Export CSV
            </Button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto rounded-md border border-border/60">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card/80 backdrop-blur">
              <tr className="border-b border-border/60 text-left">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Country</th>
                <th className="px-3 py-2">Experience</th>
                <th className="px-3 py-2">Versions</th>
              </tr>
            </thead>
            <tbody>
              {(acks?.entries ?? []).map((e) => {
                const m = (e.metadata ?? {}) as Record<string, unknown>;
                return (
                  <tr key={e.id} className="border-b border-border/60">
                    <td className="px-3 py-1.5 text-muted-foreground">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5">{e.actor_email ?? e.actor_id ?? "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{e.action}</td>
                    <td className="px-3 py-1.5 font-mono">
                      {String(m.country_selected ?? "—")}
                      {m.country_detected_from_ip ? ` (ip: ${m.country_detected_from_ip})` : ""}
                    </td>
                    <td className="px-3 py-1.5 font-mono">{String(m.experience ?? "—")}</td>
                    <td className="px-3 py-1.5 font-mono">
                      r:{String(m.risk_disclosure_version ?? "—")} · d:
                      {String(m.disclaimer_version ?? "—")}
                    </td>
                  </tr>
                );
              })}
              {(acks?.entries ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                    No acknowledgments yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="rounded-md border border-warn/30 bg-warn/10 p-3 text-[12px] text-warn">
        <div className="mb-1 flex items-center gap-1.5 font-semibold">
          <ShieldAlert className="h-3.5 w-3.5" /> Reminder
        </div>
        Disclosure copy is sourced from <code>/risk-disclosure</code> and the homepage banner
        component. Edit those files to change wording, then bump the version above to force
        re-acceptance.
      </div>
    </div>
  );
}

function CountryListEditor({
  title,
  codes,
  input,
  setInput,
  onAdd,
  onRemove,
  tone,
}: {
  title: string;
  codes: string[];
  input: string;
  setInput: (v: string) => void;
  onAdd: (v: string) => void;
  onRemove: (v: string) => void;
  tone: "warn" | "sell";
}) {
  const toneCls = tone === "warn" ? "border-warn/40 text-warn" : "border-sell/40 text-sell";
  return (
    <div className="rounded-md border border-border/60 bg-card/40 p-3">
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {codes.length === 0 && (
          <span className="text-[11px] text-muted-foreground">No countries yet.</span>
        )}
        {codes.map((c) => {
          const name = COUNTRY_LIST.find((co) => co.code === c)?.name ?? c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => onRemove(c)}
              className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] ${toneCls} hover:bg-current/10`}
              title={`${name} — click to remove`}
            >
              <span className="font-mono">{c}</span>
              <span className="text-[10px] opacity-70">×</span>
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase().slice(0, 2))}
          placeholder="ISO code (e.g. US)"
          className="h-8 text-xs"
        />
        <Button size="sm" variant="outline" onClick={() => onAdd(input)}>
          Add
        </Button>
      </div>
    </div>
  );
}
